// Drag an event to another day. Touch-first: press and hold an event to pick
// it up (moving sooner scrolls as usual); a mouse picks it up once dragged a
// few pixels. Day cells mark themselves with data-drop-day; the one under the
// finger is highlighted, and letting go there moves the event.

import { useEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import type { IsoDate } from "../../../shared/dates";
import type { PlacedEvent } from "./calendarModel";

/** Touch: hold this long to pick an event up. */
const HOLD_MS = 380;
/** Mouse: drag this far to pick up. Touch: moving this far before the hold ends scrolls instead. */
const SLOP_PX = 8;

export interface EventDrag {
  p: PlacedEvent;
  from: IsoDate;
  over: IsoDate | null;
  x: number;
  y: number;
}

interface Press {
  pointerId: number;
  mouse: boolean;
  x0: number;
  y0: number;
  x: number;
  y: number;
  p: PlacedEvent;
  from: IsoDate;
  active: boolean;
  over: IsoDate | null;
  timer?: ReturnType<typeof setTimeout>;
  release: () => void;
}

const dayAt = (x: number, y: number): IsoDate | null => (document.elementFromPoint(x, y)?.closest("[data-drop-day]") as HTMLElement | null)?.dataset.dropDay ?? null;

/** Swallow the click that follows letting go of a drag, so it doesn't open the event or the day. */
function swallowNextClick() {
  const stop = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  window.addEventListener("click", stop, { capture: true, once: true });
  setTimeout(() => window.removeEventListener("click", stop, { capture: true }), 0);
}

export function useEventDrag(canDrag: (p: PlacedEvent) => boolean, onDrop: (p: PlacedEvent, from: IsoDate, to: IsoDate) => void) {
  const [drag, setDrag] = useState<EventDrag | null>(null);
  const press = useRef<Press | null>(null);
  const ghost = useRef<HTMLDivElement | null>(null);
  const handlers = useRef({ canDrag, onDrop });
  useEffect(() => {
    handlers.current = { canDrag, onDrop };
  });
  useEffect(() => () => press.current?.release(), []);

  /** Props for an event that can be dragged from the cell for `day`. */
  const bind = (p: PlacedEvent, day: IsoDate) => ({
    "data-dragging": drag?.p.event.id === p.event.id && drag.from === day ? "" : undefined,
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (press.current || !handlers.current.canDrag(p) || (e.pointerType === "mouse" && e.button !== 0)) return;
      const mouse = e.pointerType === "mouse";

      const pickUp = () => {
        const s = press.current!;
        s.active = true;
        s.over = s.from;
        navigator.vibrate?.(12);
        setDrag({ p: s.p, from: s.from, over: s.from, x: s.x, y: s.y });
      };
      const move = (ev: PointerEvent) => {
        const s = press.current;
        if (!s || ev.pointerId !== s.pointerId) return;
        s.x = ev.clientX;
        s.y = ev.clientY;
        if (!s.active) {
          if (Math.hypot(s.x - s.x0, s.y - s.y0) <= SLOP_PX) return;
          if (!s.mouse) return s.release(); // a swipe, not a hold: let it scroll
          pickUp();
        }
        if (ghost.current) {
          ghost.current.style.left = `${s.x}px`;
          ghost.current.style.top = `${s.y}px`;
        }
        const over = dayAt(s.x, s.y);
        if (over !== s.over) {
          s.over = over;
          setDrag((d) => (d ? { ...d, over } : d));
        }
      };
      const up = (ev: PointerEvent) => {
        const s = press.current;
        if (!s || ev.pointerId !== s.pointerId) return;
        if (s.active) {
          swallowNextClick();
          const to = dayAt(ev.clientX, ev.clientY);
          if (to && to !== s.from) handlers.current.onDrop(s.p, s.from, to);
        }
        s.release();
      };
      const cancel = (ev: PointerEvent) => ev.pointerId === press.current?.pointerId && press.current.release();
      // While an event is held, a touch must not scroll the page or the list under it.
      const holdStill = (ev: TouchEvent) => press.current?.active && ev.preventDefault();

      // Window listeners keep tracking even if the chip re-renders away mid-drag.
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", cancel);
      window.addEventListener("touchmove", holdStill, { passive: false });
      press.current = {
        pointerId: e.pointerId,
        mouse,
        x0: e.clientX,
        y0: e.clientY,
        x: e.clientX,
        y: e.clientY,
        p,
        from: day,
        active: false,
        over: null,
        timer: mouse ? undefined : setTimeout(pickUp, HOLD_MS),
        release: () => {
          clearTimeout(press.current?.timer);
          window.removeEventListener("pointermove", move);
          window.removeEventListener("pointerup", up);
          window.removeEventListener("pointercancel", cancel);
          window.removeEventListener("touchmove", holdStill);
          press.current = null;
          setDrag(null);
        },
      };
    },
    // A long press would otherwise open the browser's context menu.
    onContextMenu: (e: ReactMouseEvent) => {
      if (handlers.current.canDrag(p)) e.preventDefault();
    },
  });

  const ghostEl =
    drag &&
    createPortal(
      <div ref={ghost} className="cal-drag-ghost" style={{ left: drag.x, top: drag.y, ["--ev" as string]: drag.p.event.color ?? "var(--accent)" } as CSSProperties} aria-hidden>
        {drag.p.event.displayTitle}
      </div>,
      document.body,
    );

  return { drag, bind, ghost: ghostEl };
}

export type DragControl = ReturnType<typeof useEventDrag>;

/** Class for a day cell: highlighted while an event is held over it (other than the day it came from). */
export const dropClass = (dnd: DragControl, day: IsoDate) => (dnd.drag?.over === day && dnd.drag.from !== day ? "drop-over" : "");
